import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { DataRenderer } from './DataRenderer';
import { toggleLike, deleteSharedResult } from '../../api/survey-explorer/sharedResultsApi';

export const SharedResultCard = ({ result, accessToken, onLikeToggle, currentUser, onDelete, onUseQuery }) => {
  const [isStatisticsExpanded, setIsStatisticsExpanded] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  const [likeError, setLikeError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  
  // Local state for immediate UI updates
  const [localLiked, setLocalLiked] = useState(result.user_liked);
  const [localLikeCount, setLocalLikeCount] = useState(result.like_count || 0);
  
  // Sync local state when props change (e.g., after refresh)
  useEffect(() => {
    setLocalLiked(result.user_liked);
    setLocalLikeCount(result.like_count || 0);
  }, [result.user_liked, result.like_count]);

  const handleLikeToggle = async () => {
    if (!accessToken || isLiking) return;
    
    setIsLiking(true);
    setLikeError(null);
    
    // Store original state for potential revert
    const originalLiked = localLiked;
    const originalCount = localLikeCount;
    
    // Optimistic update
    const newLikedState = !localLiked;
    const newCount = newLikedState ? localLikeCount + 1 : Math.max(localLikeCount - 1, 0);
    
    setLocalLiked(newLikedState);
    setLocalLikeCount(newCount);
    
    try {
      const response = await toggleLike(result.id, accessToken);
      
      // Sync with actual server response
      if (response.liked !== undefined && response.like_count !== undefined) {
        setLocalLiked(response.liked);
        setLocalLikeCount(response.like_count);
        
        // Also update parent state with server response
        if (onLikeToggle) {
          onLikeToggle(result.id, response.liked);
        }
      } else {
        // Fallback: trust our optimistic update
        if (onLikeToggle) {
          onLikeToggle(result.id, newLikedState);
        }
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      setLikeError('Failed to update like status');
      // Revert to original state on error
      setLocalLiked(originalLiked);
      setLocalLikeCount(originalCount);
    } finally {
      setIsLiking(false);
    }
  };

  const handleDelete = async () => {
    if (!accessToken || isDeleting) return;
    
    const confirmDelete = window.confirm('Are you sure you want to delete this shared result? This action cannot be undone.');
    if (!confirmDelete) return;
    
    setIsDeleting(true);
    setDeleteError(null);
    
    try {
      await deleteSharedResult(result.id, accessToken);
      onDelete(result.id);
    } catch (error) {
      console.error('Error deleting result:', error);
      setDeleteError('Failed to delete result');
    } finally {
      setIsDeleting(false);
    }
  };

  const toggleStatistics = () => {
    setIsStatisticsExpanded(prev => !prev);
  };

  // Process outputs to extract content
  const processedContent = {
    descriptions: [],
    charts: [],
    statistics: null
  };

  result.outputs.forEach(output => {
    if (output.output_type === 'description') {
      try {
        const content = JSON.parse(output.content);
        if (content.description) {
          processedContent.descriptions.push(content.description);
        }
      } catch (e) {
        // If not JSON, treat as plain text
        processedContent.descriptions.push(output.content);
      }
    } else if (output.output_type === 'chart') {
      try {
        const content = JSON.parse(output.content);
        if (content.chart) {
          processedContent.charts.push(content.chart);
        }
      } catch (e) {
        // If not JSON, treat as plain base64 string
        processedContent.charts.push(output.content);
      }
    } else if (output.output_type === 'statistics') {
      try {
        processedContent.statistics = JSON.parse(output.content);
      } catch (e) {
        console.error('Error parsing statistics:', e);
      }
    }
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check if current user owns this result
  // Try multiple possible ID matches since different auth systems might use different IDs
  const isOwner = currentUser && (
    result.user_id === currentUser.id ||
    result.user_id === currentUser.localAccountId ||
    result.user_id === currentUser.homeAccountId ||
    result.user_id === currentUser.username ||
    result.user_name === currentUser.name ||
    result.user_name === currentUser.username
  );

  return (
    <div className="bg-white rounded-lg shadow p-6 w-full mb-6">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-sm text-gray-500">
              {formatDate(result.created_at)}
            </span>
          </div>
          <div className="mb-3">
            <button
              onClick={() => onUseQuery && onUseQuery(result.query)}
              className="text-sm text-gray-600 hover:text-green-600 hover:underline transition-colors cursor-pointer text-left block"
              title="Click to use this query in My Results tab"
              aria-label="Use this query in My Results tab"
            >
              {result.query}
            </button>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {/* Like Button */}
          <button
            onClick={handleLikeToggle}
            disabled={isLiking}
            className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm transition-colors ${
              localLiked 
                ? 'bg-red-100 text-red-600 hover:bg-red-200' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            } ${isLiking ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <svg 
              className={`w-4 h-4 ${localLiked ? 'fill-current' : ''}`} 
              fill={localLiked ? 'currentColor' : 'none'}
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" 
              />
            </svg>
            {localLikeCount}
          </button>

          {/* Delete Button (only for owners) */}
          {isOwner && (
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className={`p-2 rounded-full text-sm transition-colors ${
                isDeleting 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-red-100 text-red-600 hover:bg-red-200'
              }`}
              title="Delete this shared result"
            >
              {isDeleting ? (
                <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Error Messages */}
      {likeError && (
        <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
          {likeError}
        </div>
      )}
      
      {deleteError && (
        <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
          {deleteError}
        </div>
      )}
      
      {/* Descriptions */}
      {processedContent.descriptions.length > 0 && (
        <div className="mb-6 text-left">
          <div className="text-gray-700 prose prose-sm max-w-none">
            <ReactMarkdown>
              {processedContent.descriptions.join('\n\n')}
            </ReactMarkdown>
          </div>
        </div>
      )}
      
      {/* Charts */}
      {processedContent.charts.length > 0 && (
        <div className="mb-6">
          <div className="space-y-4">
            {processedContent.charts.map((chart, chartIndex) => (
              <div key={chartIndex} className="overflow-auto">
                <img 
                  src={`data:image/png;base64,${chart}`} 
                  alt={`Survey data visualization ${chartIndex + 1}`} 
                  className="mx-auto"
                />
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Collapsible Statistics */}
      {processedContent.statistics && processedContent.statistics.statistics && (
        <div className="mb-6">
          <button
            onClick={toggleStatistics}
            className="flex items-center justify-between w-full p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition"
          >
            <h4 className="text-lg font-medium">Data</h4>
            <span className="text-gray-500">
              {isStatisticsExpanded ? '▼' : '▶'}
            </span>
          </button>
          
          {isStatisticsExpanded && (
            <div className="mt-2 bg-gray-50 p-4 rounded-lg overflow-auto">
              {DataRenderer.render(processedContent.statistics.statistics)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}; 